import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { useCountdown } from '@/hooks/useInactivityLogout';

interface InactivityLogoutModalProps {
  open: boolean;
  /** Total seconds the modal stays open before auto-logout. Default 60. */
  countdownSeconds?: number;
  onStay: () => void;
  onLogout: () => void;
}

/**
 * "Are you still there?" warning shown when the inactivity timer triggers.
 * Closing/dismissing is intentionally disabled — the user must explicitly
 * choose to stay or log out.
 */
export function InactivityLogoutModal({
  open,
  countdownSeconds = 60,
  onStay,
  onLogout,
}: InactivityLogoutModalProps) {
  const { t } = useTranslation();
  const remaining = useCountdown(countdownSeconds, open);

  return (
    <Modal
      open={open}
      onClose={() => {
        /* dismissal disabled — user must pick a button */
      }}
      closeOnBackdrop={false}
      closeOnEsc={false}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onLogout}>
            {t('inactivity.logoutNow')}
          </Button>
          <Button variant="primary" onClick={onStay}>
            {t('inactivity.stay')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center text-center py-2">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-warning/10 text-warning">
          <Clock size={26} />
        </div>
        <h2 className="font-heading text-lg font-bold text-deep-blue">
          {t('inactivity.title')}
        </h2>
        <p className="mt-2 text-sm text-gray">
          {t('inactivity.description')}
        </p>
        <div className="mt-5 font-mono text-3xl font-bold text-deep-blue tabular-nums">
          {String(Math.floor(remaining / 60)).padStart(1, '0')}:
          {String(remaining % 60).padStart(2, '0')}
        </div>
      </div>
    </Modal>
  );
}
